import { ref, computed } from "vue";

export interface IncomingCallContact {
  id: string;
  fname: string;
  lname: string;
  phones?: Array<{
    label: string;
    number: string;
    isValid: boolean;
    routeType: string;
    ivrExtension?: string;
    gatekeeperName?: string;
  }>;
}

export interface IncomingCallData {
  callSid: string;
  parentCallSid?: string;
  isParentCall?: boolean;
  from: string;
  to: string;
  contact: IncomingCallContact | null;
  organizationId: string;
  userId: string;
}

export interface TranscriptionMessage {
  id: string | number;
  role: string;
  text: string;
}

export type CallState = "ringing" | "active" | null;

const incomingCall = ref<IncomingCallData | null>(null);
const callState = ref<CallState>(null);
const callDuration = ref<number>(0);
const wasCallAnswered = ref<boolean>(false);
const isMinimized = ref<boolean>(false);
const enableTranscription = ref<boolean>(true);
const enableAISummary = ref<boolean>(true);
const transcriptionMessages = ref<TranscriptionMessage[]>([]);
// role → message id; findIndex lookup avoids stale index after trim
const partialCaptions = ref(new Map<string, string | number>());
// `${role}:${segmentId}` keys; blocks late interims after a final is committed
const finalizedSegments = ref(new Set<string>());
let messageIdCounter = 0;
const parentCallSid = ref<string | null>(null);
const childCallSid = ref<string | null>(null);
let activeConnection: any = null;
let callTimerInterval: ReturnType<typeof setInterval> | null = null;
let callStartTime: number | null = null;

export function useIncomingCall() {
  const hasIncomingCall = computed(() => !!incomingCall.value);

  const callerName = computed(() => {
    if (!incomingCall.value) return null;
    if (incomingCall.value.contact) {
      return `${incomingCall.value.contact.fname} ${incomingCall.value.contact.lname}`;
    }
    return null;
  });

  const callerPhone = computed(() => {
    return incomingCall.value?.from || null;
  });

  const formattedCallDuration = computed(() => {
    const hours = Math.floor(callDuration.value / 3600);
    const minutes = Math.floor((callDuration.value % 3600) / 60);
    const seconds = callDuration.value % 60;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  });

  const setIncomingCall = (data: IncomingCallData) => {
    incomingCall.value = data;
    parentCallSid.value = data.parentCallSid || data.callSid;
    childCallSid.value = null;
    callState.value = "ringing";
    callDuration.value = 0;
    wasCallAnswered.value = false;
    isMinimized.value = false;
    transcriptionMessages.value = [];
    partialCaptions.value = new Map();
    finalizedSegments.value = new Set();
  };

  const setChildCallSid = (callSid: string) => {
    childCallSid.value = callSid;
  };

  const isCallSidMatch = (callSid: string): boolean => {
    const match = callSid === parentCallSid.value || callSid === childCallSid.value;
    return match;
  };

  // Sets callSid tracking for outbound calls (Dialer) without triggering incoming call UI
  const setOutboundCallSid = (callSid: string) => {
    parentCallSid.value = callSid;
    childCallSid.value = null;
    transcriptionMessages.value = [];
    partialCaptions.value = new Map();
    finalizedSegments.value = new Set();
  };

  const clearOutboundCallSid = () => {
    // Only clear if there's no active incoming call (avoid clearing incoming call state)
    if (!incomingCall.value) {
      parentCallSid.value = null;
      childCallSid.value = null;
      transcriptionMessages.value = [];
      partialCaptions.value = new Map();
      finalizedSegments.value = new Set();
    }
  };

  const setCallActive = () => {
    if (callState.value === "ringing") {
      callState.value = "active";
      wasCallAnswered.value = true;
      startCallTimer();
    }
  };

  const startCallTimer = () => {
    if (callTimerInterval) {
      clearInterval(callTimerInterval);
    }
    callStartTime = Date.now();
    callDuration.value = 0;

    callTimerInterval = setInterval(() => {
      if (callStartTime) {
        callDuration.value = Math.floor((Date.now() - callStartTime) / 1000);
      }
    }, 1000);
  };

  const stopCallTimer = () => {
    if (callTimerInterval) {
      clearInterval(callTimerInterval);
      callTimerInterval = null;
    }
    callStartTime = null;
    callDuration.value = 0;
  };

  const clearIncomingCall = () => {
    const answered = wasCallAnswered.value;
    incomingCall.value = null;
    callState.value = null;
    wasCallAnswered.value = false;
    isMinimized.value = false;
    transcriptionMessages.value = [];
    partialCaptions.value = new Map();
    finalizedSegments.value = new Set();
    parentCallSid.value = null;
    childCallSid.value = null;
    activeConnection = null;
    stopCallTimer();
    return answered;
  };

  const clearCallIfMatches = (callSid: string) => {
    if (incomingCall.value?.callSid === callSid) {
      return clearIncomingCall();
    }
    return false;
  };

  const minimizeCall = () => {
    if (callState.value === "active" || callState.value === "ringing") {
      isMinimized.value = true;
    }
  };

  const maximizeCall = () => {
    isMinimized.value = false;
  };

  const setActiveConnection = (connection: any) => {
    activeConnection = connection;
  };

  const getActiveConnection = () => {
    return activeConnection;
  };

  const rememberFinalized = (segKey: string | null) => {
    if (!segKey) return;
    finalizedSegments.value.add(segKey);
    if (finalizedSegments.value.size > 200) {
      const first = finalizedSegments.value.values().next().value;
      if (first !== undefined) finalizedSegments.value.delete(first);
    }
  };

  const trimIfNeeded = () => {
    if (transcriptionMessages.value.length > 50) {
      transcriptionMessages.value = transcriptionMessages.value.slice(-50);
      partialCaptions.value.clear();
    }
  };

  const updatePartialTranscription = (role: string, text: string, segmentId?: string) => {
    // drop late interims for a segment whose final already committed
    if (segmentId && finalizedSegments.value.has(`${role}:${segmentId}`)) return;

    const existingId = partialCaptions.value.get(role);
    if (existingId !== undefined) {
      const idx = transcriptionMessages.value.findIndex((m) => m.id === existingId);
      if (idx !== -1) {
        transcriptionMessages.value[idx].text = text;
        return;
      }
    }
    const id = `partial-${role}-${messageIdCounter++}`;
    transcriptionMessages.value.push({ id, role, text });
    partialCaptions.value.set(role, id);
  };

  const finalizeTranscriptionMessage = (role: string, text: string, segmentId?: string) => {
    const segKey = segmentId ? `${role}:${segmentId}` : null;
    const existingId = partialCaptions.value.get(role);

    if (existingId !== undefined) {
      const idx = transcriptionMessages.value.findIndex((m) => m.id === existingId);
      if (idx !== -1) {
        transcriptionMessages.value[idx].text = text;
        partialCaptions.value.delete(role);
        rememberFinalized(segKey);
        trimIfNeeded();
        return;
      }
    }

    transcriptionMessages.value.push({ id: messageIdCounter++, role, text });
    rememberFinalized(segKey);
    trimIfNeeded();
  };

  const setEnableTranscription = (value: boolean) => {
    enableTranscription.value = value;
    if (!value) {
      enableAISummary.value = false;
    }
  };

  const setEnableAISummary = (value: boolean) => {
    if (value && !enableTranscription.value) {
      return;
    }
    enableAISummary.value = value;
  };

  return {
    incomingCall,
    callState,
    callDuration,
    formattedCallDuration,
    hasIncomingCall,
    callerName,
    callerPhone,
    wasCallAnswered,
    isMinimized,
    enableTranscription,
    enableAISummary,
    transcriptionMessages,
    parentCallSid,
    childCallSid,
    setIncomingCall,
    setCallActive,
    setChildCallSid,
    isCallSidMatch,
    startCallTimer,
    stopCallTimer,
    clearIncomingCall,
    clearCallIfMatches,
    minimizeCall,
    maximizeCall,
    setActiveConnection,
    getActiveConnection,
    updatePartialTranscription,
    finalizeTranscriptionMessage,
    setOutboundCallSid,
    clearOutboundCallSid,
    setEnableTranscription,
    setEnableAISummary,
  };
}
